import React, { useEffect, useMemo, useState } from "react";
import ExpressionField from "./ExpressionField";
import interiorParameterApi from "../../services/interiorParameterApi";

function parseScopes(value) {
  try {
    if (Array.isArray(value)) return value;
    return JSON.parse(value || "[]");
  } catch {
    return [];
  }
}

function getParamDefinition(definitions = [], key) {
  return definitions.find((p) => String(p.param_key) === String(key)) || null;
}

export default function ParameterManagerModal({
  isOpen,
  onClose,
  resolvedNode,
  onUpdateNode,
  projectParameters = {},
  onChangeProjectParameters,
  typeParameters,
  onChangeTypeParameters,
}) {
  const [activeTab, setActiveTab] = useState("resolved");
  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [parameterDefinitions, setParameterDefinitions] = useState([]);
  const [loadingDefinitions, setLoadingDefinitions] = useState(false);

  const [newParam, setNewParam] = useState({
    paramKey: "",
    value: "",
    dataType: "number",
    unit: "",
  });

  const [newParamValidation, setNewParamValidation] = useState({
  ok: true,
  value: "",
  message: "",
});

const [formValidation, setFormValidation] = useState({ ok: true, errors: [] });

  useEffect(() => {
    if (!isOpen) return;

    let alive = true;

    async function loadDefinitions() {
      try {
        setLoadingDefinitions(true);
        const res = await interiorParameterApi.listDefinitions();
        if (alive) setParameterDefinitions(res.items || []);
      } catch (err) {
        console.error("load parameter definitions error:", err);
      } finally {
        if (alive) setLoadingDefinitions(false);
      }
    }

    loadDefinitions();

    return () => {
      alive = false;
    };
  }, [isOpen]);

  if (!isOpen || !resolvedNode) return null;

  const {
    effectiveParameters = {},
    resolvedParameters = [],
    parameterDebug = {},
    node,
    definition,
  } = resolvedNode;

  const {
    definitionDefaults = definition?.defaultParameters || {},
    parameterBindings =
  node?.parameterBindings ||
  resolvedNode?.parameterBindings ||
  {},

parameterOverrides =
  node?.parameterOverrides ||
  resolvedNode?.parameterOverrides ||
  {},
  } = parameterDebug || {};

  const activeTypeParameters =
    typeParameters || definitionDefaults || definition?.defaultParameters || {};

  const availableDefinitionsForTab = useMemo(() => {
    return (parameterDefinitions || []).filter((p) =>
      parseScopes(p.allowed_scopes).includes(activeTab)
    );
  }, [parameterDefinitions, activeTab]);

  const availableParameterKeys = useMemo(() => {
    const keysFromRegistry = availableDefinitionsForTab.map((p) => p.param_key);
    const keysFromResolved = Object.keys(effectiveParameters || {});
    return Array.from(new Set([...keysFromRegistry, ...keysFromResolved]));
  }, [availableDefinitionsForTab, effectiveParameters]);

  const projectParams = Object.entries(projectParameters || {}).map(([key, value]) => ({
    key,
    value,
    source: "project",
    type: typeof value,
  }));

 const typeParams = Object.entries(activeTypeParameters || {}).map(([key, value]) => {
  const def = getParamDefinition(parameterDefinitions, key);

  return {
    key,
    value,
    source: "type",
    type: def?.data_type || typeof value,
    unit: def?.unit || "",
    group: def?.group_name || "Other",
  };
});

const currentOverrides =
  resolvedNode?.node?.parameterOverrides ||
  resolvedNode?.parameterOverrides ||
  parameterOverrides ||
  {};

const instanceParams = Object.entries(effectiveParameters || {}).map(
  ([key, effectiveValue]) => {
    const hasOverride = Object.prototype.hasOwnProperty.call(
      currentOverrides,
      key
    );

    const def = getParamDefinition(parameterDefinitions, key);
    const value = hasOverride ? currentOverrides[key] : effectiveValue;

    return {
      key,
      value,
      effectiveValue,
      source: hasOverride ? "instance" : "inherited",
      type: def?.data_type || typeof value,
      unit: def?.unit || "",
      group: def?.group_name || "Other",
      hasOverride,
    };
  }
);

  const bindingParams = Object.entries(parameterBindings || {}).map(([key, value]) => ({
    key,
    value,
    source: "binding",
    type: typeof value,
  }));

  const constraints =
  resolvedNode?.node?.constraints ||
  resolvedNode?.constraints ||
  definition?.constraints ||
  {};

const constraintParams = Object.entries(constraints || {}).map(([key, value]) => {
  const def = getParamDefinition(parameterDefinitions, key);

  return {
    key,
    value,
    source: "constraint",
    type: def?.data_type || typeof value,
    unit: def?.unit || "",
    group: def?.group_name || "Other",
  };
});

  const resolvedParams =
    Array.isArray(resolvedParameters) && resolvedParameters.length
      ? resolvedParameters.map((p) => ({
          key: p.key,
          value: p.value,
          source: p.source || "resolved",
          type: typeof p.value,
        }))
      : Object.entries(effectiveParameters || {}).map(([key, value]) => ({
          key,
          value,
          source: "resolved",
          type: typeof value,
        }));

  const debugParams = Object.entries(effectiveParameters || {}).map(([key, value]) => ({
    key,
    value,
    source: "effective",
    type: typeof value,
  }));

  function getActiveDataset() {
    switch (activeTab) {
      case "project":
        return projectParams;
      case "type":
        return typeParams;
      case "instance":
        return instanceParams;
      case "bindings":
        return bindingParams;
        case "constraints":
  return constraintParams;
      case "resolved":
        return resolvedParams;
      case "debug":
        return debugParams;
      default:
        return [];
    }
  }

  const filteredParams = useMemo(() => {
    const q = search.trim().toLowerCase();
    return getActiveDataset().filter((p) => {
      if (!q) return true;
      return (
        String(p.key || "").toLowerCase().includes(q) ||
        String(p.value || "").toLowerCase().includes(q)
      );
    });
  }, [activeTab, search, resolvedNode, projectParameters, activeTypeParameters]);

  const hasInstance = !!resolvedNode?.node?.id;

  useEffect(() => {
  if (!isOpen) return;

  if (!resolvedNode?.node?.id) {
    setActiveTab("project");
  } else if (activeTab === "project") {
    setActiveTab("instance");
  }
}, [isOpen, resolvedNode?.node?.id]);

  function handleAddParameter() {
    const first = availableDefinitionsForTab[0];

    setNewParam({
      paramKey: first?.param_key || "",
      value: first?.default_value || "",
      dataType: first?.data_type || "number",
      unit: first?.unit || "",
    });

    setShowAddModal(true);
  }

  function confirmAddParameter() {
    const key = String(newParam.paramKey || "").trim();
    if (!key) return;

    let value = newParam.value;

    if (newParam.dataType === "number" && !Number.isNaN(Number(value))) {
      value = Number(value);
    }

    if (activeTab === "project") {
      onChangeProjectParameters?.({
        ...(projectParameters || {}),
        [key]: value,
      });
      setShowAddModal(false);
      return;
    }

    if (activeTab === "type") {
      if (typeof onChangeTypeParameters !== "function") {
        alert("Type parameters are read-only here. Open definition editor.");
        return;
      }

      onChangeTypeParameters({
        ...(activeTypeParameters || {}),
        [key]: value,
      });

      setShowAddModal(false);
      return;
    }

    if (activeTab === "constraints") {
  if (!node?.id) {
    alert("Select an instance first.");
    return;
  }

  onUpdateNode(node.id, {
    constraints: {
      ...(node.constraints || {}),
      [key]: value,
    },
  });

  setShowAddModal(false);
  return;
}

    if (activeTab === "instance") {
      if (!node?.id) {
        alert("Select an instance first.");
        return;
      }

      onUpdateNode(node.id, {
        parameterOverrides: {
          ...(node.parameterOverrides || {}),
          [key]: value,
        },
      });

      setShowAddModal(false);
      return;
    }

    if (activeTab === "bindings") {
      if (!node?.id) {
        alert("Select an instance first.");
        return;
      }

      onUpdateNode(node.id, {
        parameterBindings: {
          ...(node.parameterBindings || {}),
          [key]: value,
        },
      });

      setShowAddModal(false);
    }
  }

  function handleDeleteParameter(key) {
    if (activeTab === "project") {
      const updated = { ...(projectParameters || {}) };
      delete updated[key];
      onChangeProjectParameters?.(updated);
      return;
    }

    if (activeTab === "type") {
      const updated = { ...(activeTypeParameters || {}) };
      delete updated[key];
      onChangeTypeParameters?.(updated);
      return;
    }

    if (!node?.id) return;

    if (activeTab === "instance") {
      const updated = { ...(node.parameterOverrides || {}) };
      delete updated[key];
      onUpdateNode(node.id, { parameterOverrides: updated });
    }

    if (activeTab === "bindings") {
      const updated = { ...(node.parameterBindings || {}) };
      delete updated[key];
      onUpdateNode(node.id, { parameterBindings: updated });
    }

    if (activeTab === "constraints") {
  const updated = { ...(node.constraints || {}) };
  delete updated[key];
  onUpdateNode(node.id, { constraints: updated });
  return;
}
  }

  function handleChangeValue(key, value) {
    if (activeTab === "project") {
      onChangeProjectParameters?.({
        ...(projectParameters || {}),
        [key]: value,
      });
      return;
    }

    if (activeTab === "type") {
      onChangeTypeParameters?.({
        ...(activeTypeParameters || {}),
        [key]: value,
      });
      return;
    }

    if (activeTab === "constraints") {
  if (!node?.id) return;

  onUpdateNode(node.id, {
    constraints: {
      ...(node.constraints || {}),
      [key]: value,
    },
  });

  return;
}

    if (!node?.id) return;

    if (activeTab === "instance") {
      onUpdateNode(node.id, {
        parameterOverrides: {
          ...(node.parameterOverrides || {}),
          [key]: value,
        },
      });
    }

    if (activeTab === "bindings") {
      onUpdateNode(node.id, {
        parameterBindings: {
          ...(node.parameterBindings || {}),
          [key]: value,
        },
      });
    }
  }

  const canEdit =
    activeTab === "project" ||
    (activeTab === "type" && typeof onChangeTypeParameters === "function") ||
    activeTab === "instance" ||
activeTab === "bindings" ||
activeTab === "constraints";

    function toggleInstanceOverride(key, enabled) {
  if (!node?.id) return;

  const updated = { ...(node.parameterOverrides || {}) };

  if (enabled) {
    updated[key] = effectiveParameters?.[key] ?? "";
  } else {
    delete updated[key];
  }

  onUpdateNode(node.id, {
    parameterOverrides: updated,
  });
}

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="w-[1100px] h-[680px] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
        <div className="h-12 border-b px-4 flex items-center justify-between bg-slate-50">
          <div>
            <div className="font-semibold text-slate-800">Parameter Manager</div>
            <div className="text-xs text-slate-500">
              {resolvedNode.name || resolvedNode.id}
            </div>
          </div>

          <button onClick={onClose} className="px-3 py-1.5 border rounded bg-white text-sm">
            Close
          </button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-[220px_minmax(0,1fr)]">
          <aside className="border-r bg-slate-50 flex flex-col">
            {[
  ["project", "Project"],
  ["type", "Type"],
  ...(hasInstance
    ? [
        ["instance", "Instance"],
        ["bindings", "Bindings"],
        ["constraints", "Constraints"],
      ]
    : []),
  ["resolved", "Resolved"],
  ["debug", "Debug"],
].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`text-left px-4 py-3 text-sm border-b ${
                  activeTab === key
                    ? "bg-white text-blue-700 font-semibold border-l-4 border-l-blue-600"
                    : "text-slate-600 hover:bg-white"
                }`}
              >
                {label}
              </button>
            ))}
          </aside>

          <main className="min-h-0 flex flex-col">
            <div className="h-14 border-b px-4 flex items-center justify-between gap-3">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search parameters..."
                className="w-80 px-3 py-2 border rounded text-sm"
              />

              {canEdit && (
                <button
                  type="button"
                  onClick={handleAddParameter}
                  className="px-3 py-2 rounded bg-blue-600 text-white text-sm"
                >
                  + Add parameter
                </button>
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-auto p-4">
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 border-b">Key</th>
                      <th className="text-left p-2 border-b">Value / Expression</th>
                      <th className="text-left p-2 border-b">Source</th>
                      <th className="text-left p-2 border-b">Override</th>
                      <th className="text-left p-2 border-b">Type</th>
                      <th className="text-left p-2 border-b w-[160px]">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredParams.length ? (
                      filteredParams.map((param) => {
                        const editable =
  activeTab === "project" ||
  (activeTab === "type" &&
    typeof onChangeTypeParameters === "function") ||
  activeTab === "instance" ||
  activeTab === "bindings" ||
  activeTab === "constraints";

                        return (
                          <tr key={`${activeTab}_${param.key}`} className="border-b align-top">
                            <td className="p-2 font-mono text-xs">{param.key}</td>

                            <td className="p-2">
                              {editable ? (
                                <ExpressionField
  value={param.value ?? ""}
  onChange={(nextValue) =>
    handleChangeValue(param.key, nextValue)
  }
  availableParams={availableParameterKeys}
  previewParams={effectiveParameters}
  placeholder="Value or formula"
  disabled={activeTab === "instance" && !param.hasOverride}
/>
                              ) : (
                                <span className="font-mono text-xs">
                                  {String(param.value ?? "")}
                                </span>
                              )}
                            </td>

                            <td className="p-2">
                              <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700">
                                {param.source}
                              </span>
                            </td>

                            <td className="p-2">
  {activeTab === "instance" ? (
    <label className="flex items-center gap-2 text-xs">
      <input
        type="checkbox"
        checked={!!param.hasOverride}
        onChange={(e) =>
          toggleInstanceOverride(param.key, e.target.checked)
        }
      />
      {param.hasOverride ? (
        <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">
          override
        </span>
      ) : (
        <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-500">
          inherited
        </span>
      )}
    </label>
  ) : (
    <span className="text-xs text-slate-400">—</span>
  )}
</td>

                            <td className="p-2 text-slate-500">
  {param.type}
  {param.unit ? (
    <span className="ml-1 text-slate-400">({param.unit})</span>
  ) : null}
</td>

                            <td className="p-2">
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => navigator.clipboard?.writeText(param.key)}
                                  className="px-2 py-1 rounded border bg-white text-xs"
                                >
                                  Copy key
                                </button>

                                {editable && (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteParameter(param.key)}
                                    className="px-2 py-1 rounded border bg-white text-xs text-red-600"
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-slate-400">
                          No parameters found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {activeTab === "debug" && (
                <pre className="mt-4 text-xs bg-slate-900 text-slate-100 p-4 rounded overflow-auto">
                  {JSON.stringify(
                    {
                      effectiveParameters,
                      resolvedParameters,
                      parameterDebug,
                      node,
                      definition,
                      parameterDefinitions,
                    },
                    null,
                    2
                  )}
                </pre>
              )}
            </div>
          </main>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center">
          <div className="w-[560px] bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="h-12 border-b px-4 flex items-center justify-between bg-slate-50">
              <div className="font-semibold">Add Parameter</div>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-3 py-1.5 border rounded bg-white text-sm"
              >
                Close
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Parameter</label>

                <select
                  value={newParam.paramKey}
                  onChange={(e) => {
                    const def = availableDefinitionsForTab.find(
                      (p) => p.param_key === e.target.value
                    );

                    setNewParam({
                      paramKey: e.target.value,
                      value: def?.default_value || "",
                      dataType: def?.data_type || "number",
                      unit: def?.unit || "",
                    });
                  }}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">Select parameter...</option>
                  {availableDefinitionsForTab.map((param) => (
                    <option key={param.id} value={param.param_key}>
                      {param.label} ({param.param_key})
                      {param.unit ? ` · ${param.unit}` : ""}
                    </option>
                  ))}
                </select>

                {loadingDefinitions && (
                  <div className="text-xs text-slate-400 mt-1">Loading parameters...</div>
                )}
              </div>

              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Value / Expression
                </label>

              <ExpressionField
  value={newParam.value ?? ""}
  onChange={(nextValue) =>
    setNewParam((p) => ({ ...p, value: nextValue }))
  }
  availableParams={availableParameterKeys}
  previewParams={effectiveParameters}
  placeholder="Value or formula"
/>
              </div>

              {!newParamValidation.ok && (
  <div className="text-xs text-red-600 mt-1">
    {newParamValidation.message || "Invalid expression."}
  </div>
)}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Type</label>
                  <input
                    value={newParam.dataType}
                    readOnly
                    className="w-full px-3 py-2 border rounded bg-slate-50"
                  />
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">Unit</label>
                  <input
                    value={newParam.unit || ""}
                    readOnly
                    className="w-full px-3 py-2 border rounded bg-slate-50"
                  />
                </div>
              </div>
            </div>

            <div className="border-t px-4 py-3 flex justify-end gap-2 bg-slate-50">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-3 py-2 border rounded bg-white"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmAddParameter}
                disabled={
  !newParam.paramKey ||
  String(newParam.value ?? "").trim() === "" ||
  !newParamValidation.ok
}
                className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}